import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";

import { getEventLocations } from "../../redux/eventLocation/eventLocationSlice";
import {
  assignLocation,
  addUpdateSponsorQuota,
  getSponsorQuota,
  removeLocation,
  getUserInfo,
} from "../../redux/userSpeaker/userSpeakerSlice";

const AssignLocationComponent = ({ userId, isLoggedInSponsor }) => {
  const dispatch = useDispatch();

  const { locations = [] } = useSelector((state) => state.eventLocation);
  const { userInfo, sponsorQuota } = useSelector((state) => state.speaker);

  const [confirmAssign, setConfirmAssign] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmEditQuota, setConfirmEditQuota] = useState(null);
  const [quota, setQuota] = useState("");

  useEffect(() => {
    dispatch(getEventLocations());
    dispatch(getUserInfo(userId));
    dispatch(getSponsorQuota(userId));
  }, [dispatch, userId]);

  const locationOptions = locations.map((loc) => ({
    value: loc.id,
    label: loc.name,
  }));

  const currentLocations = userInfo?.eventLocations || [];
  const isSponsor = userInfo?.Role?.name === "SPONSOR";
  const quotaList =
    userInfo?.quotas ||
    userInfo?.sponsorQuotas ||
    userInfo?.SponsorQuotas ||
    sponsorQuota ||
    [];

  const getLocationQuota = (loc) => {
    if (loc?.quota || loc?.quota === 0) return loc.quota;

    const item = quotaList.find(
      (q) => q.locationId === loc.id || q.eventLocationId === loc.id
    );

    return item?.quota ?? item?.assignedQuota ?? item?.totalQuota ?? "-";
  };

  return (
    <div className="assign-location-wrapper">

      {/* HEADER */}
      <div className="card shadow-sm border-0 rounded-4 p-20 mb-20">
        <div className="d-flex justify-content-between align-items-center mb-16">
          <div>
            <h5 className="fw-bold mb-4">Assigned Locations</h5>
            <span className="text-muted small">
              Manage user event locations
            </span>
          </div>

          {!isLoggedInSponsor && (
            <div style={{ width: 280 }}>
              <Select
                options={locationOptions}
                placeholder="Assign new location..."
                classNamePrefix="react-select"
                onChange={(selected) => {
                  if (!selected) return;

                  const location = locations.find(
                    (l) => l.id === selected.value
                  );

                  setConfirmAssign(location);
                  setQuota("");
                }}
              />
            </div>
          )}
        </div>

        {/* LOCATION LIST */}
        {currentLocations.length === 0 ? (
          <div className="text-center py-30 text-muted">
            <Icon icon="mdi:map-marker-off" width="40" />
            <p className="mt-10 mb-0">No locations assigned</p>
          </div>
        ) : (
          <div className="d-flex flex-wrap gap-12">
            {currentLocations.map((loc) => (
              <div
                key={loc.id}
                className="location-chip d-flex align-items-center"
                onClick={() => {
                  if (isLoggedInSponsor || !isSponsor) return;

                  setConfirmEditQuota(loc);
                  setQuota(
                    getLocationQuota(loc) === "-"
                      ? ""
                      : String(getLocationQuota(loc))
                  );
                }}
                style={{ cursor: isLoggedInSponsor || !isSponsor ? "default" : "pointer" }}
              >
                <Icon icon="mdi:map-marker" width="18" />

                <span className="mx-8">
                  {loc.name}{isSponsor && ` - Quota: ${getLocationQuota(loc)}`}
                </span>

                {!isLoggedInSponsor && (
                  <Icon
                    icon="mdi:close-circle"
                    className="remove-icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmRemove(loc);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ASSIGN MODAL */}
      {confirmAssign && (
        <div className="custom-modal">
          <div className="modal-content-box">

            <div className="modal-header">
              <h6>Assign Location</h6>
              <Icon
                icon="mdi:close"
                className="cursor-pointer"
                onClick={() => setConfirmAssign(null)}
              />
            </div>

            <div className="modal-body text-center">
              <Icon icon="mdi:map-marker-radius" width="50" color="#4CAF50" />
              <p className="mt-10">Assign this location?</p>
              <h6 className="fw-bold">{confirmAssign.name}</h6>
              {isSponsor && (
                <input
                  className="form-control mt-16"
                  placeholder="Enter Quota (max 99)"
                  value={quota}
                  maxLength={2}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (!value || Number(value) <= 99) {
                      setQuota(value);
                    }
                  }}
                />
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-light"
                onClick={() => setConfirmAssign(null)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (isSponsor && !quota) {
                    toast.error("Quota is required");
                    return;
                  }

                  try {
                    await dispatch(
                      assignLocation({
                        userId,
                        locationId: confirmAssign.id,
                      })
                    ).unwrap();
                    if (isSponsor) {
                      await dispatch(
                        addUpdateSponsorQuota({
                          sponsorId: userId,
                          locationId: confirmAssign.id,
                          quota: Number(quota),
                        })
                      ).unwrap();
                    }

                    toast.success("Location assigned");
                    setConfirmAssign(null);
                    setQuota("");
                    dispatch(getUserInfo(userId));
                    dispatch(getSponsorQuota(userId));
                  } catch (err) {
                    toast.error(err?.message || "Failed");
                  }
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT QUOTA MODAL */}
      {confirmEditQuota && isSponsor && (
        <div className="custom-modal">
          <div className="modal-content-box">

            <div className="modal-header">
              <h6>Update Quota</h6>
              <Icon
                icon="mdi:close"
                className="cursor-pointer"
                onClick={() => setConfirmEditQuota(null)}
              />
            </div>

            <div className="modal-body text-center">
              <Icon icon="mdi:pencil-circle" width="50" color="#4CAF50" />
              <p className="mt-10">Update quota limit</p>
              <h6 className="fw-bold">{confirmEditQuota.name}</h6>
              {isSponsor && (
                <input
                  className="form-control mt-16"
                  placeholder="Enter Quota (max 99)"
                  value={quota}
                  maxLength={2}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (!value || Number(value) <= 99) {
                      setQuota(value);
                    }
                  }}
                />
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-light"
                onClick={() => setConfirmEditQuota(null)}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (isSponsor && !quota) {
                    toast.error("Quota is required");
                    return;
                  }

                  try {
                    await dispatch(
                      addUpdateSponsorQuota({
                        sponsorId: userId,
                        locationId: confirmEditQuota.id,
                        quota: Number(quota),
                      })
                    ).unwrap();

                    toast.success("Quota updated");
                    setConfirmEditQuota(null);
                    setQuota("");
                    dispatch(getUserInfo(userId));
                    dispatch(getSponsorQuota(userId));
                  } catch (err) {
                    toast.error(err?.message || "Failed");
                  }
                }}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* REMOVE MODAL */}
      {confirmRemove && (
        <div className="custom-modal danger">
          <div className="modal-content-box">

            <div className="modal-header">
              <h6>Remove Location</h6>
              <Icon
                icon="mdi:close"
                onClick={() => setConfirmRemove(null)}
              />
            </div>

            <div className="modal-body text-center">
              <Icon icon="mdi:alert-circle" width="50" color="#f44336" />
              <p className="mt-10">Remove this location?</p>
              <h6 className="fw-bold">{confirmRemove.name}</h6>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-light"
                onClick={() => setConfirmRemove(null)}
              >
                Cancel
              </button>

              <button
                className="btn btn-danger"
                onClick={async () => {
                  try {
                    await dispatch(
                      removeLocation({
                        userId,
                        locationId: confirmRemove.id,
                      })
                    ).unwrap();

                    toast.success("Location removed");
                    setConfirmRemove(null);
                    dispatch(getUserInfo(userId));
                  } catch (err) {
                    toast.error(err?.message || "Failed");
                  }
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignLocationComponent;
